(() => {
    const overlay = document.getElementById("allEscortsMapOverlay");
    if (!overlay) return;

    const sheet = overlay.querySelector(".all-escorts-map-overlay__sheet");
    const mapElement = document.getElementById("allEscortsMap");
    const listElement = overlay.querySelector(".all-escorts-map-overlay__list");
    const countElement = overlay.querySelector(
        ".all-escorts-map-overlay__count",
    );
    const statusElement = overlay.querySelector(
        ".all-escorts-map-overlay__status",
    );
    const loadMoreButton = overlay.querySelector(
        ".all-escorts-map-overlay__load-more",
    );
    const filterTarget = overlay.querySelector(
        ".all-escorts-map-overlay__filter-target",
    );
    const closeButton = overlay.querySelector(
        ".all-escorts-map-overlay__close",
    );
    const viewToggle = overlay.querySelector(
        ".all-escorts-map-overlay__view-toggle",
    );
    const viewToggleLabel = viewToggle.querySelector("span");
    const listCloseButton = overlay.querySelector(
        ".all-escorts-map-overlay__list-close",
    );
    const mobileFilterButton = overlay.querySelector(
        ".all-escorts-map-overlay__filter-button",
    );
    const searchInput = overlay.querySelector(
        ".all-escorts-map-overlay__search-input",
    );
    const locateButton = overlay.querySelector(
        ".all-escorts-map-overlay__locate",
    );
    const launchers = document.querySelectorAll(".all-escorts-map-launcher");
    const sidebarPanel = document.querySelector(".all-escorts-sidebar__panel");
    const filterDrawer = document.getElementById("fbarDrawer");
    const hasFilters = Boolean(sidebarPanel || filterDrawer);
    overlay.classList.toggle("has-filters", hasFilters);
    mobileFilterButton.hidden = !filterDrawer;

    let map = null;
    let infoWindow = null;
    let autocomplete = null;
    let mapsPromise = null;
    let activeLauncher = null;
    let previousScrollY = 0;
    let fetchController = null;
    let fetchTimer = null;
    let mapIdleTimer = null;
    let suppressIdleRefreshUntil = 0;
    let filterPlaceholder = null;
    let drawerPlaceholder = null;
    let locations = [];
    let nextPage = null;
    let requestSequence = 0;
    let inertElements = [];
    const markers = new Map();
    const profileCards = new Map();

    const escapeHtml = (value) =>
        String(value ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

    const isDesktop = () => window.matchMedia("(min-width: 992px)").matches;

    function moveFilterPanel() {
        if (!sidebarPanel) return;
        if (!filterPlaceholder) {
            filterPlaceholder = document.createComment(
                "all-escorts-sidebar-panel",
            );
            sidebarPanel.parentNode.insertBefore(
                filterPlaceholder,
                sidebarPanel,
            );
        }

        if (overlay.classList.contains("is-open") && isDesktop()) {
            filterTarget.appendChild(sidebarPanel);
        } else if (filterPlaceholder.parentNode) {
            filterPlaceholder.parentNode.insertBefore(
                sidebarPanel,
                filterPlaceholder.nextSibling,
            );
        }
    }

    function moveFilterDrawer() {
        if (!filterDrawer) return;
        if (!drawerPlaceholder) {
            drawerPlaceholder = document.createComment(
                "all-escorts-filter-drawer",
            );
            filterDrawer.parentNode.insertBefore(
                drawerPlaceholder,
                filterDrawer,
            );
        }

        if (overlay.classList.contains("is-open") && !isDesktop()) {
            overlay.appendChild(filterDrawer);
        } else if (drawerPlaceholder.parentNode) {
            drawerPlaceholder.parentNode.insertBefore(
                filterDrawer,
                drawerPlaceholder.nextSibling,
            );
        }
    }

    function isolateModal() {
        inertElements = [];
        let branch = overlay;
        while (branch.parentElement && branch.parentElement !== document.body) {
            Array.from(branch.parentElement.children).forEach((sibling) => {
                if (
                    sibling === branch ||
                    sibling.inert ||
                    sibling.matches(".pac-container")
                ) return;
                sibling.inert = true;
                inertElements.push(sibling);
            });
            branch = branch.parentElement;
        }
        Array.from(document.body.children).forEach((sibling) => {
            // Places appends its suggestions outside the dialog. Keep them
            // interactive on subsequent opens, just like the search field.
            if (
                sibling === branch ||
                sibling.inert ||
                sibling.matches(".pac-container")
            ) return;
            sibling.inert = true;
            inertElements.push(sibling);
        });
    }

    function restoreModalIsolation() {
        inertElements.forEach((element) => {
            element.inert = false;
        });
        inertElements = [];
    }

    function lockPage() {
        previousScrollY = window.scrollY;
        document.body.style.position = "fixed";
        document.body.style.top = `-${previousScrollY}px`;
        document.body.style.width = "100%";
        document.body.classList.add("all-escorts-map-open");
    }

    function unlockPage() {
        document.body.classList.remove("all-escorts-map-open");
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        window.scrollTo(0, previousScrollY);
    }

    function setMobileMapMode(showMap) {
        overlay.classList.toggle("is-map-mode", showMap);
        viewToggle.setAttribute("aria-pressed", showMap ? "true" : "false");
        viewToggleLabel.textContent = showMap ? "Show list" : "Show map";
        const resultsPane = overlay.querySelector(
            ".all-escorts-map-overlay__results",
        );
        const mapPane = overlay.querySelector(
            ".all-escorts-map-overlay__map-wrap",
        );
        resultsPane.inert = !isDesktop() && showMap;
        mapPane.inert = !isDesktop() && !showMap;
        if (showMap && map && window.google) {
            setTimeout(() => {
                google.maps.event.trigger(map, "resize");
            }, 280);
        }
    }

    async function loadGoogleMaps() {
        if (window.google && window.google.maps) {
            if (
                !window.google.maps.places &&
                typeof window.google.maps.importLibrary === "function"
            ) {
                await window.google.maps.importLibrary("places");
            }
            if (!window.google.maps.places) {
                throw new Error("Google Places failed to load");
            }
            return;
        }
        if (mapsPromise) return mapsPromise;

        mapsPromise = new Promise((resolve, reject) => {
            const callbackName = `initGalleryMapOverlay_${Date.now()}`;
            window[callbackName] = () => {
                delete window[callbackName];
                resolve();
            };

            const script = document.createElement("script");
            const params = new URLSearchParams({
                key: overlay.dataset.mapKey || "",
                callback: callbackName,
                libraries: "places",
                loading: "async",
                v: "weekly",
            });
            script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
            script.async = true;
            script.defer = true;
            script.onerror = () => {
                delete window[callbackName];
                mapsPromise = null;
                reject(new Error("Google Maps failed to load"));
            };
            document.head.appendChild(script);
        });

        return mapsPromise;
    }

    function initialiseMap() {
        if (map) return;

        map = new google.maps.Map(mapElement, {
            center: { lat: 51.5053235, lng: -0.1702399 },
            zoom: isDesktop() ? 13 : 12,
            mapId: overlay.dataset.mapId || undefined,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            zoomControl: true,
            zoomControlOptions: {
                position: google.maps.ControlPosition.RIGHT_CENTER,
            },
            clickableIcons: false,
            gestureHandling: "greedy",
        });
        infoWindow = new google.maps.InfoWindow({
            headerDisabled: true,
        });

        map.addListener("idle", () => {
            clearTimeout(mapIdleTimer);
            if (Date.now() < suppressIdleRefreshUntil) return;
            mapIdleTimer = setTimeout(refreshResults, 250);
        });

        if (google.maps.places && searchInput) {
            autocomplete = new google.maps.places.Autocomplete(searchInput, {
                componentRestrictions: { country: "GB" },
                fields: ["formatted_address", "geometry", "name"],
            });
            autocomplete.addListener("place_changed", () => {
                const place = autocomplete.getPlace();
                if (!place.geometry) {
                    statusElement.hidden = false;
                    statusElement.textContent =
                        "Choose a location from the suggestions.";
                    return;
                }
                searchInput.value =
                    place.formatted_address || place.name || searchInput.value;
                if (place.geometry.viewport) {
                    map.fitBounds(place.geometry.viewport);
                } else {
                    map.setCenter(place.geometry.location);
                    map.setZoom(14);
                }
            });
        }
    }

    async function applyLauncherMapPosition() {
        const contextualLauncher = activeLauncher?.dataset.mapInitialQuery
            ? activeLauncher
            : document.querySelector('.all-escorts-map-launcher[data-map-initial-query]');
        const pageContext = document.querySelector('[data-gallery-map-initial-query]');
        const query = contextualLauncher?.dataset.mapInitialQuery
            || pageContext?.dataset.galleryMapInitialQuery;
        if (!query) return;

        searchInput.value = contextualLauncher?.dataset.mapInitialLabel
            || pageContext?.dataset.galleryMapInitialLabel
            || query;
        const result = await new Promise((resolve) => {
            new google.maps.Geocoder().geocode(
                { address: query, componentRestrictions: { country: "GB" } },
                (results, status) =>
                    resolve(status === "OK" ? results?.[0] : null),
            );
        });
        if (!result?.geometry?.location) return;

        suppressIdleRefreshUntil = Date.now() + 1000;
        map.setCenter(result.geometry.location);
        map.setZoom(
            Number(contextualLauncher?.dataset.mapInitialZoom)
            || Number(pageContext?.dataset.galleryMapInitialZoom)
            || 14
        );
    }

    function useBrowserLocation() {
        if (!navigator.geolocation) {
            statusElement.hidden = false;
            statusElement.textContent =
                "Location is not supported. Search for an area instead.";
            return;
        }

        locateButton.disabled = true;
        locateButton.classList.add("is-locating");
        statusElement.hidden = false;
        statusElement.textContent = "Finding your location…";

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const coordinates = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                };
                searchInput.value = "Current location";
                map.panTo(coordinates);
                map.setZoom(14);
                locateButton.disabled = false;
                locateButton.classList.remove("is-locating");
            },
            (error) => {
                locateButton.disabled = false;
                locateButton.classList.remove("is-locating");
                statusElement.hidden = false;
                statusElement.textContent =
                    error.code === error.PERMISSION_DENIED
                        ? "Location access is off. Search for an area instead."
                        : "Your location could not be found. Search for an area instead.";
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
        );
    }

    function currentFilters() {
        const checkedValues = (selector) =>
            Array.from(document.querySelectorAll(selector))
                .filter((input) => input.checked)
                .map((input) => input.value);

        return {
            baseCategory:
                document.querySelector("div.category")?.textContent.trim() ||
                "",
            baseService:
                document.querySelector("div.service")?.textContent.trim() || "",
            baseLocation:
                document.querySelector("div.location")?.textContent.trim() ||
                "",
            services: checkedValues("input.service"),
            category: checkedValues("input.category"),
            incallLocation: checkedValues("input.incall-location"),
            hairColour: checkedValues("input.hair_colour"),
            nationality: checkedValues("input.nationality"),
            dresses: checkedValues("input.dress"),
            fetishes: checkedValues("input.fetish"),
            toys: checkedValues("input.toy"),
            allFetishes: checkedValues("input.fetish-all"),
            ages: [
                document.querySelector('input[name="age_min"]')?.value || "",
                document.querySelector('input[name="age_max"]')?.value || "",
            ].filter(Boolean),
            sort:
                document.querySelector('input[name="sortby"]:checked')?.value ||
                "",
        };
    }

    function markerIcon(isActive = false, count = 1) {
        const colour = isActive ? "#111111" : "#ff385c";
        const pin = `<path fill="${colour}" stroke="#fff" stroke-width="2" d="M17 1C8.2 1 1 8.2 1 17c0 12 16 24 16 24s16-12 16-24C33 8.2 25.8 1 17 1Z"/><circle cx="17" cy="17" r="5" fill="#fff"/>`;
        if (count < 2) {
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="42" viewBox="0 0 34 42">${pin}</svg>`;
            return {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
                scaledSize: new google.maps.Size(34, 42),
                anchor: new google.maps.Point(17, 42),
            };
        }
        // Several escorts at exactly the same position: pin plus a count badge.
        const badgeColour = isActive ? "#ff385c" : "#111111";
        const label = String(count);
        const badgeWidth = Math.max(22, 10 + label.length * 7);
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="50" viewBox="0 0 48 50"><g transform="translate(4 8)">${pin}</g><rect x="${46 - badgeWidth}" y="1" width="${badgeWidth}" height="22" rx="11" fill="${badgeColour}" stroke="#fff" stroke-width="2"/><text x="${46 - badgeWidth / 2}" y="16.5" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="700" fill="#fff">${label}</text></svg>`;
        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
            scaledSize: new google.maps.Size(48, 50),
            anchor: new google.maps.Point(21, 50),
        };
    }

    // Escorts sharing exactly the same position are shown as one pin with a count.
    function groupLocations(items) {
        const groups = new Map();
        items.forEach((item) => {
            const key = `${Number(item.position.lat).toFixed(6)},${Number(item.position.lng).toFixed(6)}`;
            if (!groups.has(key)) groups.set(key, { key, items: [] });
            groups.get(key).items.push(item);
        });
        return Array.from(groups.values());
    }

    // Pins keyed by position, so "load more" and small refreshes keep unchanged
    // pins (and any preview open on them) instead of rebuilding everything.
    const markersByKey = new Map();
    let activeProfileId = null;
    // Position key of the pin whose preview is open, so a pin rebuilt with new
    // members (e.g. after "load more") gets its preview re-opened, not dropped.
    let openPreviewKey = null;

    function clearMarkers() {
        closePreviewWindow();
        markersByKey.forEach((marker) => marker.setMap(null));
        markersByKey.clear();
        markers.clear();
    }

    function closePreviewWindow() {
        openPreviewKey = null;
        if (infoWindow) infoWindow.close();
    }

    function openPreview(content, marker) {
        // Opening a preview auto-pans the map; that must not trigger a refresh
        // (which rebuilds the pins and closes the preview).
        suppressIdleRefreshUntil = Math.max(suppressIdleRefreshUntil, Date.now() + 1500);
        openPreviewKey = marker.positionKey || null;
        infoWindow.setContent(content);
        infoWindow.open({ map, anchor: marker });
    }

    function renderProfileAd(location, marker) {
        if (!infoWindow) return;

        const content = document.createElement("article");
        content.className = "all-escorts-map-info";

        const imageLink = document.createElement("a");
        imageLink.className = "all-escorts-map-info__image";
        imageLink.href = location.profile_url;
        imageLink.setAttribute("aria-label", `View ${location.title}`);

        const image = document.createElement("img");
        image.src = location.image;
        image.alt = location.title;
        imageLink.appendChild(image);

        const body = document.createElement("div");
        body.className = "all-escorts-map-info__body";

        const name = document.createElement("strong");
        name.textContent = location.title;

        const area = document.createElement("span");
        area.textContent = location.area || "London";

        const availability = document.createElement("span");
        availability.className = location.available
            ? "all-escorts-map-info__available"
            : "all-escorts-map-info__away";
        availability.textContent = location.available
            ? "Available now"
            : "Currently away";

        const rate = document.createElement("span");
        rate.className = "all-escorts-map-info__rate";
        rate.textContent = location.price
            ? `£${location.price} / hour`
            : "View rates";

        const profileLink = document.createElement("a");
        profileLink.className = "all-escorts-map-info__link";
        profileLink.href = location.profile_url;
        profileLink.textContent = "View escort";

        const closePreview = document.createElement("button");
        closePreview.type = "button";
        closePreview.className = "all-escorts-map-info__close";
        closePreview.setAttribute("aria-label", "Close profile preview");
        closePreview.innerHTML =
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
        closePreview.addEventListener("click", closePreviewWindow);

        body.append(name, area, availability, rate, profileLink);
        content.append(imageLink, body, closePreview);
        openPreview(content, marker);
    }

    function renderGroupAd(items, marker) {
        if (!infoWindow) return;

        const content = document.createElement("article");
        content.className = "all-escorts-map-group";

        const head = document.createElement("header");
        head.className = "all-escorts-map-group__head";
        const title = document.createElement("strong");
        title.textContent = `${items.length} escorts at this location`;
        const closePreview = document.createElement("button");
        closePreview.type = "button";
        closePreview.className = "all-escorts-map-group__close";
        closePreview.setAttribute("aria-label", "Close preview");
        closePreview.innerHTML =
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
        closePreview.addEventListener("click", closePreviewWindow);
        head.append(title, closePreview);

        const list = document.createElement("ul");
        list.className = "all-escorts-map-group__list";
        items.forEach((location) => {
            const row = document.createElement("li");
            const link = document.createElement("a");
            link.className = "all-escorts-map-group__item";
            link.href = location.profile_url;
            const image = document.createElement("img");
            image.src = location.image;
            image.alt = "";
            image.loading = "lazy";
            const body = document.createElement("span");
            body.className = "all-escorts-map-group__body";
            const name = document.createElement("strong");
            name.textContent = location.title;
            const meta = document.createElement("span");
            meta.textContent = `${location.area || "London"} · ${location.available ? "Available now" : "Currently away"}`;
            const rate = document.createElement("span");
            rate.className = "all-escorts-map-group__rate";
            rate.textContent = location.price
                ? `£${location.price} / hour`
                : "View rates";
            body.append(name, meta, rate);
            link.append(image, body);
            row.appendChild(link);
            list.appendChild(row);
        });

        content.append(head, list);
        openPreview(content, marker);
    }

    function scrollProfileIntoList(card) {
        const listRect = listElement.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        listElement.scrollTo({
            top: Math.max(
                0,
                listElement.scrollTop + cardRect.top - listRect.top - 12,
            ),
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
                .matches
                ? "auto"
                : "smooth",
        });
    }

    function setActiveProfile(id, panToMarker = false) {
        const activeId = String(id);
        activeProfileId = activeId;
        profileCards.forEach((profileCard, profileId) => {
            const isActive = profileId === activeId;
            profileCard.classList.toggle("is-active", isActive);
            profileCard.setAttribute(
                "aria-current",
                isActive ? "true" : "false",
            );
        });
        markersByKey.forEach((marker) => {
            const isActive = marker.memberIds.includes(activeId);
            marker.setIcon(markerIcon(isActive, marker.memberIds.length));
            marker.setZIndex(isActive ? 10 : 1);
        });

        const card = profileCards.get(activeId);
        if (card) scrollProfileIntoList(card);

        const marker = markers.get(activeId);
        if (panToMarker && marker) {
            suppressIdleRefreshUntil = Date.now() + 2000;
            map.panTo(marker.getPosition());
            if (map.getZoom() < 14) map.setZoom(14);
        }
    }

    function renderList() {
        profileCards.clear();
        if (!locations.length) {
            listElement.innerHTML = `
                <div class="all-escorts-map-overlay__empty">
                    <strong>No escorts in this map area</strong>
                    <span>Move or zoom out on the map to see more profiles.</span>
                </div>
            `;
            return;
        }

        listElement.innerHTML = locations
            .map((location) => {
                const price = location.price
                    ? `£${escapeHtml(location.price)} / hour`
                    : "View rates";
                const availability = location.available
                    ? '<span class="all-escorts-map-profile__available">Available</span>'
                    : '<span class="all-escorts-map-profile__away">Away</span>';

                return `
                <article class="all-escorts-map-profile" data-map-profile-id="${escapeHtml(location.id)}">
                    <button type="button" class="all-escorts-map-profile__focus" aria-label="Show ${escapeHtml(location.title)} on map"></button>
                    <span class="all-escorts-map-profile__image">
                        <img src="${escapeHtml(location.image)}" alt="" loading="lazy" decoding="async">
                    </span>
                    <div class="all-escorts-map-profile__body">
                        <strong class="all-escorts-map-profile__name">${escapeHtml(location.title)}</strong>
                        ${availability}
                        <span class="all-escorts-map-profile__area">${escapeHtml(location.area || "London")}</span>
                        <span class="all-escorts-map-profile__price">${price}</span>
                    </div>
                </article>
            `;
            })
            .join("");
        listElement
            .querySelectorAll(".all-escorts-map-profile")
            .forEach((card, index) => {
                const id = String(locations[index].id);
                card.id = `all-escorts-map-profile-${index}`;
                card.setAttribute("aria-current", "false");
                profileCards.set(id, card);
            });
    }

    function renderMarkers() {
        const seen = new Set();
        markers.clear();
        groupLocations(locations).forEach((group) => {
            const memberIds = group.items.map((item) => String(item.id));
            const memberKey = memberIds.join(",");
            seen.add(group.key);
            let marker = markersByKey.get(group.key);
            const reopenPreview =
                Boolean(marker) &&
                marker.memberKey !== memberKey &&
                openPreviewKey === group.key;
            if (marker && marker.memberKey !== memberKey) {
                marker.setMap(null);
                marker = null;
            }
            if (!marker) {
                const isCluster = group.items.length > 1;
                const first = group.items[0];
                const isActive = memberIds.includes(activeProfileId);
                marker = new google.maps.Marker({
                    position: {
                        lat: Number(first.position.lat),
                        lng: Number(first.position.lng),
                    },
                    map,
                    title: isCluster
                        ? `${group.items.length} escorts at this location`
                        : first.title,
                    icon: markerIcon(isActive, group.items.length),
                    zIndex: isActive ? 10 : 1,
                });
                marker.positionKey = group.key;
                marker.memberKey = memberKey;
                marker.memberIds = memberIds;
                marker.groupItems = group.items;
                marker.set(
                    "ariaLabel",
                    isCluster
                        ? `Show ${group.items.length} escorts at this location in results`
                        : `Show ${first.title} in results`,
                );
                marker.addListener("click", () => selectMarker(marker));
                markersByKey.set(group.key, marker);
                if (reopenPreview) selectMarker(marker);
            }
            memberIds.forEach((id) => markers.set(id, marker));
        });
        markersByKey.forEach((marker, key) => {
            if (seen.has(key)) return;
            if (openPreviewKey === key) openPreviewKey = null;
            marker.setMap(null);
            markersByKey.delete(key);
        });
    }

    function selectMarker(marker) {
        const items = marker.groupItems || [];
        if (items.length > 1) {
            setActiveProfile(items[0].id);
            renderGroupAd(items, marker);
        } else if (items.length === 1) {
            setActiveProfile(items[0].id);
            renderProfileAd(items[0], marker);
        }
    }

    function handleMarkerControlClick(event) {
        if (
            !overlay.classList.contains("is-open") ||
            !(event.target instanceof Element)
        )
            return;
        const markerControl = event.target.closest('[role="button"][title]');
        if (!markerControl) return;

        const controlTitle = markerControl.getAttribute("title");
        const marker = Array.from(markersByKey.values()).find(
            (candidate) => candidate.getTitle() === controlTitle,
        );
        if (!marker) return;

        event.stopImmediatePropagation();
        selectMarker(marker);
    }

    async function refreshResults(append = false) {
        if (!map || !overlay.classList.contains("is-open")) return;
        const bounds = map.getBounds();
        if (!bounds) return;
        if (append && !nextPage) return;

        if (fetchController) fetchController.abort();
        fetchController = new AbortController();
        const currentController = fetchController;
        const currentRequest = ++requestSequence;
        const centre = map.getCenter();
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();
        const filters = currentFilters();

        statusElement.hidden = false;
        const requestedPage = append ? nextPage : 1;
        statusElement.textContent = append
            ? "Loading more profiles…"
            : "Updating profiles…";
        loadMoreButton.disabled = true;

        try {
            const response = await fetch(overlay.dataset.resultsUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "X-CSRF-TOKEN":
                        window.csrfToken ||
                        document.querySelector('meta[name="csrf-token"]')
                            ?.content ||
                        "",
                },
                body: new URLSearchParams({
                    compact: "1",
                    page: String(requestedPage),
                    ne_lat: northEast.lat(),
                    ne_lng: northEast.lng(),
                    sw_lat: southWest.lat(),
                    sw_lng: southWest.lng(),
                    lat: centre.lat(),
                    lng: centre.lng(),
                    radius: "12000",
                    services: filters.services.join(","),
                    base_service: filters.baseService,
                    category: filters.category.join(","),
                    base_category: filters.baseCategory,
                    base_location: filters.baseLocation,
                    incall_location: filters.incallLocation.join(","),
                    hair_colour: filters.hairColour.join(","),
                    nationality: filters.nationality.join(","),
                    dresses: filters.dresses.join(","),
                    fetishes: filters.fetishes.join(","),
                    toys: filters.toys.join(","),
                    all_fetishes: filters.allFetishes.join(","),
                    ages: filters.ages.join(","),
                    sort: filters.sort,
                }),
                signal: fetchController.signal,
            });

            if (!response.ok)
                throw new Error(`Map results returned ${response.status}`);
            const data = await response.json();
            if (
                currentRequest !== requestSequence ||
                currentController !== fetchController
            )
                return;
            const receivedLocations = Array.isArray(data.locations)
                ? data.locations
                : [];
            locations = append
                ? Array.from(
                      new Map(
                          locations
                              .concat(receivedLocations)
                              .map((location) => [
                                  String(location.id),
                                  location,
                              ]),
                      ).values(),
                  )
                : receivedLocations;
            nextPage = data.has_more ? Number(data.next_page) : null;
            const total = Number(data.count) || locations.length;
            countElement.textContent =
                total > locations.length
                    ? `Showing ${locations.length} of ${total}`
                    : `${locations.length} ${locations.length === 1 ? "escort" : "escorts"}`;
            renderList();
            renderMarkers();
            loadMoreButton.hidden = !nextPage;
            loadMoreButton.disabled = false;
            statusElement.hidden = true;
        } catch (error) {
            if (error.name === "AbortError") return;
            if (
                currentRequest !== requestSequence ||
                currentController !== fetchController
            )
                return;
            loadMoreButton.disabled = false;
            if (append) {
                statusElement.hidden = false;
                statusElement.textContent =
                    "More profiles could not be loaded. Please try again.";
                return;
            }
            locations = [];
            nextPage = null;
            clearMarkers();
            loadMoreButton.hidden = true;
            countElement.textContent = "Unavailable";
            listElement.innerHTML = `
                <div class="all-escorts-map-overlay__empty">
                    <strong>We couldn't update the map</strong>
                    <button type="button" class="all-escorts-map-overlay__retry">Try again</button>
                </div>
            `;
            statusElement.textContent = "Map results could not be loaded.";
        }
    }

    function scheduleRefresh() {
        if (!overlay.classList.contains("is-open")) return;
        clearTimeout(fetchTimer);
        fetchTimer = setTimeout(refreshResults, 450);
    }

    async function openOverlay(event) {
        event.preventDefault();
        activeLauncher = event.currentTarget;
        lockPage();
        overlay.inert = false;
        overlay.setAttribute("aria-hidden", "false");
        overlay.classList.add("is-open");
        moveFilterPanel();
        moveFilterDrawer();
        isolateModal();
        setMobileMapMode(true);
        sheet.focus({ preventScroll: true });

        try {
            await loadGoogleMaps();
            initialiseMap();
            await applyLauncherMapPosition();
            // Users can type while Places is still loading. Replay the current
            // query once autocomplete is ready so no extra keystroke is needed.
            if (
                document.activeElement === searchInput &&
                searchInput.value.trim()
            ) {
                searchInput.dispatchEvent(new Event("input", { bubbles: true }));
            }

            setTimeout(() => {
                google.maps.event.trigger(map, "resize");
                refreshResults();
            }, 320);
        } catch (error) {
            statusElement.textContent =
                "The map could not be loaded. Please try again.";
        }
    }

    function closeOverlay() {
        if (!overlay.classList.contains("is-open")) return;
        requestSequence++;
        if (fetchController) fetchController.abort();
        if (filterDrawer?.classList.contains("show")) {
            filterDrawer.querySelector(".fbar-drawer-close")?.click();
        }
        overlay.classList.remove("is-open");
        moveFilterPanel();
        moveFilterDrawer();
        restoreModalIsolation();
        unlockPage();
        activeLauncher?.focus({ preventScroll: true });
        overlay.inert = true;
        overlay.setAttribute("aria-hidden", "true");
    }

    launchers.forEach((launcher) =>
        launcher.addEventListener("click", openOverlay),
    );
    closeButton.addEventListener("click", closeOverlay);
    viewToggle.addEventListener("click", () => {
        setMobileMapMode(!overlay.classList.contains("is-map-mode"));
    });
    listCloseButton.addEventListener("click", () => setMobileMapMode(true));
    loadMoreButton.addEventListener("click", () => refreshResults(true));
    locateButton.addEventListener("click", useBrowserLocation);
    mobileFilterButton.addEventListener("click", () => {
        document.querySelector(".fbar-filters-btn")?.click();
    });

    listElement.addEventListener("click", (event) => {
        const retry = event.target.closest(".all-escorts-map-overlay__retry");
        if (retry) {
            refreshResults();
            return;
        }
        const focusButton = event.target.closest(
            ".all-escorts-map-profile__focus",
        );
        if (!focusButton) return;
        const card = focusButton.closest(".all-escorts-map-profile");
        if (!isDesktop()) setMobileMapMode(true);
        const id = card.dataset.mapProfileId;
        const marker = markers.get(id);
        const location = locations.find((item) => String(item.id) === id);
        if (!marker || !location) return;
        setActiveProfile(id, true);
        // Even at a shared location, opening a list result shows the selected
        // person's preview rather than the whole group.
        renderProfileAd(location, marker);
    });

    document.addEventListener("change", (event) => {
        if (event.target.matches('.filter-list input, input[name="sortby"]'))
            scheduleRefresh();
    });
    document.addEventListener("click", (event) => {
        if (
            event.target.closest(
                ".fbar-svc-proxy, .fbar-cat-proxy, .fbar-clear-block button, .clear_filter, .fbar-drawer-reset",
            )
        ) {
            scheduleRefresh();
        }
    });
    document.addEventListener("click", handleMarkerControlClick, true);

    document.addEventListener(
        "keydown",
        (event) => {
            if (!overlay.classList.contains("is-open")) return;
            if (
                event.key === "Escape" &&
                !filterDrawer?.classList.contains("show")
            ) {
                closeOverlay();
                return;
            }
            if (event.key !== "Tab") return;
            const focusable = Array.from(
                overlay.querySelectorAll(
                    "button:not([disabled]), a[href], input:not([disabled])",
                ),
            ).filter((element) => {
                const styles = window.getComputedStyle(element);
                return (
                    element.getClientRects().length > 0 &&
                    styles.visibility !== "hidden" &&
                    !element.closest("[inert]")
                );
            });
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        },
        true,
    );

    window.addEventListener(
        "resize",
        debounce(() => {
            moveFilterPanel();
            moveFilterDrawer();
            setMobileMapMode(overlay.classList.contains("is-map-mode"));
        }, 120),
    );

    function debounce(callback, wait) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => callback(...args), wait);
        };
    }
})();
